/**
 * FinalGradeScore bands from dbo.vw_ReturnInspectionVehicleCost.
 * The preview adds each edited line's SalesGradeValue delta and ignores
 * authorization status.
 */
export const finalGradeScoreFromPoints = (
  points: number,
): number | undefined => {
  if (points >= 0 && points <= 25) return 1;
  if (points >= 26 && points <= 199) return 2;
  if (points >= 200 && points <= 499) return 3;
  if (points >= 500 && points <= 1099) return 4;
  if (points > 1100) return 5;
  return undefined;
};

const finiteOr = (value: number | null | undefined, fallback: number) => {
  if (value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export type VehicleGradeEstimate = {
  originalScore?: number;
  latestScore?: number;
  originalPoints?: number;
  latestPoints?: number;
};

export type FinalGradeLineInput = {
  currentSalesGradeValue?: number | null;
  nextSalesGradeValue?: number | null;
};

const salesGradeDelta = (line: FinalGradeLineInput) => {
  const current = finiteOr(line.currentSalesGradeValue, 0);
  if (
    line.nextSalesGradeValue === null ||
    line.nextSalesGradeValue === undefined
  ) {
    return 0;
  }
  return finiteOr(line.nextSalesGradeValue, current) - current;
};

export const estimateFinalGradeFromLines = ({
  finalGradeScore,
  finalGradePoints,
  lines,
}: {
  finalGradeScore?: number | null;
  finalGradePoints?: number | null;
  lines: FinalGradeLineInput[];
}): VehicleGradeEstimate => {
  const originalScore = finiteOr(finalGradeScore, Number.NaN);
  const originalPoints = finiteOr(finalGradePoints, Number.NaN);
  const hasScore = Number.isFinite(originalScore);
  const hasPoints = Number.isFinite(originalPoints);

  if (!hasPoints) {
    return {
      originalScore: hasScore ? originalScore : undefined,
      latestScore: hasScore ? originalScore : undefined,
    };
  }

  const delta = lines.reduce((sum, line) => sum + salesGradeDelta(line), 0);
  const latestPoints = originalPoints + delta;

  return {
    originalScore: hasScore ? originalScore : undefined,
    latestScore:
      delta === 0
        ? hasScore
          ? originalScore
          : finalGradeScoreFromPoints(latestPoints)
        : finalGradeScoreFromPoints(latestPoints),
    originalPoints,
    latestPoints,
  };
};

export const estimateVehicleGrade = ({
  finalGradeScore,
  finalGradePoints,
  currentSalesGradeValue,
  nextSalesGradeValue,
}: {
  finalGradeScore?: number | null;
  finalGradePoints?: number | null;
  currentSalesGradeValue?: number | null;
  nextSalesGradeValue?: number | null;
}): VehicleGradeEstimate =>
  estimateFinalGradeFromLines({
    finalGradeScore,
    finalGradePoints,
    lines: [{ currentSalesGradeValue, nextSalesGradeValue }],
  });
